import { EXPORT_TIME_BUDGET_MS } from './collage'
import type {
  CollageWorkerRequest,
  CollageWorkerResponse,
} from './types'

export type ExportFailureCode =
  | Extract<CollageWorkerResponse, { ok: false }>['code']
  | 'worker_failed'
  | 'timeout'
  | 'cancelled'

interface WorkerLike {
  onmessage: ((event: MessageEvent<CollageWorkerResponse>) => void) | null
  onerror: ((event: ErrorEvent) => unknown) | null
  postMessage: (request: CollageWorkerRequest) => void
  terminate: () => void
}

interface WorkerExportDependencies {
  createWorker?: () => WorkerLike
  setTimer?: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>
  clearTimer?: (timer: ReturnType<typeof setTimeout>) => void
  now?: () => number
}

interface WorkerExportCallbacks {
  success: (blob: Blob, durationMs: number) => void
  error: (code: ExportFailureCode) => void
}

export interface WorkerExportOwner {
  cancel: () => void
}

export function startCollageWorkerExport(
  request: CollageWorkerRequest,
  callbacks: WorkerExportCallbacks,
  dependencies: WorkerExportDependencies = {},
): WorkerExportOwner {
  const setTimer = dependencies.setTimer ?? setTimeout
  const clearTimer = dependencies.clearTimer ?? clearTimeout
  const now = dependencies.now ?? performance.now.bind(performance)
  const startedAt = now()
  let worker: WorkerLike | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let settled = false

  const cleanup = () => {
    if (timer !== null) {
      clearTimer(timer)
      timer = null
    }
    if (worker) {
      worker.onmessage = null
      worker.onerror = null
      worker.terminate()
      worker = null
    }
  }
  const fail = (code: ExportFailureCode) => {
    if (settled) return
    settled = true
    cleanup()
    callbacks.error(code)
  }
  const succeed = (blob: Blob) => {
    if (settled) return
    settled = true
    cleanup()
    callbacks.success(blob, Math.max(0, now() - startedAt))
  }

  try {
    const createdWorker: WorkerLike = dependencies.createWorker?.() ?? new Worker(
      new URL('../worker/collage.worker.ts', import.meta.url),
      { type: 'module' },
    )
    worker = createdWorker
    createdWorker.onmessage = (event) => {
      if (event.data.ok) succeed(event.data.blob)
      else fail(event.data.code)
    }
    createdWorker.onerror = () => fail('worker_failed')
    timer = setTimer(() => fail('timeout'), EXPORT_TIME_BUDGET_MS)
    createdWorker.postMessage(request)
  } catch {
    fail('worker_failed')
  }

  return { cancel: () => fail('cancelled') }
}
